/**
 * A number that ticks. The flash rule lives here and nowhere else.
 *
 * **Flash on change of the rendered value, never on receipt** — plan §2 item 5,
 * components.md §4.3. The distinction is the whole component. A quiet market
 * re-sends the same price on every heartbeat; a cell that flashes on arrival
 * strobes continuously and teaches the user to ignore it, which costs exactly
 * the signal the flash exists to give. So the comparison is against the string
 * that is currently on screen, not against the payload that produced it: a
 * price that moves from `0.6300` to `0.63004` renders `0.6300` both times and
 * does not flash, and that is correct.
 *
 * **The flash direction never comes from a float.** Invariant 6 is absolute — no
 * float touches a balance or a price — so the caller passes `compare`, an exact
 * value it already has: a `bigint` from `parseFixed` for money and shares, or an
 * integer for cents. Deriving a direction by `parseFloat`-ing the rendered
 * string would put a float on the price path to decide a background colour,
 * which is not a trade worth making. With no `compare` and no `direction` the
 * cell simply never flashes, which is right for a market id or a timestamp.
 *
 * **It updates the class, not the tree.** The decision runs in a layout effect
 * against two refs and writes one `classList` — no state, no second render. The
 * remove/reflow/add dance restarts an animation that is still running, because a
 * price that ticks twice inside 420ms has to flash twice.
 *
 * **The orderbook ladder does not use this.** Plan §2.3: the ladder is not
 * reconciled — fixed rows, mutated directly. One forced reflow per changed cell
 * is fine for a positions table and is not fine for 50 rows at 131 f/s.
 */

import { useLayoutEffect, useRef } from 'react'
import type { ReactNode } from 'react'
import { cx } from '@/lib/cx'

export type FlashDirection = 'up' | 'down'

export interface NumCellProps {
  /** The already-formatted string. Formatting belongs to `lib/format`. */
  value: string
  /**
   * An exact, ordered value used only to choose the flash colour. `bigint` for
   * money and shares (`parseFixed`), integer for cents. Never a float.
   */
  compare?: bigint | number
  /** Overrides `compare`. For a value whose direction is not its magnitude. */
  direction?: FlashDirection
  /** Characters of width to reserve, so the column cannot reflow as digits change. */
  reserve?: number
  align?: 'left' | 'right'
  /** Off for a value that changes without meaning a move — an id, a countdown. */
  flash?: boolean
  className?: string
  title?: string
  children?: ReactNode
}

function directionOf(
  prev: bigint | number | undefined,
  next: bigint | number | undefined,
): FlashDirection | null {
  if (prev === undefined || next === undefined) return null
  if (typeof prev !== typeof next) return null
  if (next > prev) return 'up'
  if (next < prev) return 'down'
  return null
}

export function NumCell({
  value,
  compare,
  direction,
  reserve,
  align = 'right',
  flash = true,
  className,
  title,
  children,
}: NumCellProps) {
  const ref = useRef<HTMLSpanElement>(null)
  const prevValue = useRef<string | null>(null)
  const prevCompare = useRef<bigint | number | undefined>(undefined)

  useLayoutEffect(() => {
    const el = ref.current
    const seen = prevValue.current
    const lastCompare = prevCompare.current
    prevValue.current = value
    prevCompare.current = compare

    if (!el || !flash) return
    // First paint is not a change. Flashing every cell on mount would make a
    // cold load look like a burst of activity that did not happen.
    if (seen === null) return
    // The rule, in one line: the *rendered* value has to differ.
    if (seen === value) return

    const dir = direction ?? directionOf(lastCompare, compare)
    if (!dir) return

    const cls = dir === 'up' ? 'ud-flash-up' : 'ud-flash-down'
    el.classList.remove('ud-flash-up', 'ud-flash-down')
    // Force a style flush so re-adding the class restarts an animation that is
    // still mid-flight. Without it a second tick inside 420ms is invisible.
    void el.offsetWidth
    el.classList.add(cls)
  }, [value, compare, direction, flash])

  return (
    <span
      ref={ref}
      title={title}
      data-value={value}
      className={cx(
        'ud-num inline-block tabular-nums',
        align === 'right' ? 'text-right' : 'text-left',
        className,
      )}
      style={reserve ? { minWidth: `${reserve}ch` } : undefined}
    >
      {children ?? value}
    </span>
  )
}
